import { Chip, Input, Label, ListBox, Select, TextField } from '@heroui/react'
import { nativeName } from '../lib/i18n/customer'
import type { LanguageCode } from '../lib/i18n/languages'
import type { ReactNode } from 'react'
import { limitOf } from '../lib/ticketFields'
import { nameIn, type StaffKey } from '../lib/staffText'
import { NOTE_LIMIT, type Priority, type VisitField } from '../lib/types'
import { useStaffText } from '../ui/StaffText'
import { PriorityChoice } from './receptionPriority'

/** What the preview's paper shows: nothing yet, the ticket about to print, or the one printed. */
export interface ReceiptProps {
  kind: 'empty' | 'draft' | 'ticket'
  label: string
  branch: string
  number?: ReactNode
  service?: string
  priority?: Priority
  then?: string[]
  date: string
  time: string
  position?: string
  wait?: string
  /** Said instead of place and wait once the ticket stopped waiting. */
  status?: string
  code: boolean
  /** The address of the slip design's logo; none draws no logo. */
  logo?: string
  /** The print line under the paper: printing, printed, did not print. */
  meta?: { text: string; failed: boolean }
  state?: string
}

/** A paper slip mock: the branch, the number, the facts, the code. */
export function Receipt(p: ReceiptProps) {
  const { say } = useStaffText()
  return (
    <div className="rx-slipw">
      <article className="rx-slip" aria-label={p.label} data-state={p.state} data-kind={p.kind}>
        {p.logo && <img className="rx-slip__logo" src={p.logo} alt="" />}
        <span className="rx-slip__br">{say('reception.slip.where', { branch: p.branch })}</span>
        {p.kind === 'empty' ? (
          <p className="rx-slip__prompt">{say('reception.previewPrompt')}</p>
        ) : (
          <>
            <span className="rx-slip__no">{p.number}</span>
            <span className="rx-slip__svc">{p.service}</span>
            {p.priority && p.priority !== 'standard' && (
              <Chip size="sm" variant="soft" color="warning">
                {p.priority === 'vip' ? say('common.vip') : say('common.priority')}
              </Chip>
            )}
            {p.then && p.then.length > 0 && <span className="rx-slip__then">{say('reception.then', { list: p.then.join(', ') })}</span>}
          </>
        )}
        <dl className="rx-slip__facts">
          <Row label="reception.slip.dateTime" value={`${p.time}, ${p.date}`} />
          {p.status ? (
            <Row label="reception.slip.status" value={p.status} />
          ) : (
            <>
              <Row label="reception.slip.position" value={p.position || '—'} />
              <Row label="reception.slip.wait" value={p.wait || '—'} />
            </>
          )}
        </dl>
        {p.code && <QrMark />}
        <span className="rx-slip__thanks">{say('reception.slip.thanks')}</span>
      </article>
      {p.meta && (
        <p className="rx-slip__meta" data-failed={p.meta.failed || undefined}>
          {p.meta.text}
        </p>
      )}
    </div>
  )
}

function Row({ label, value }: { label: StaffKey; value: string }) {
  const { say } = useStaffText()
  return (
    <div>
      <dt>{say(label)}</dt>
      <dd>{value}</dd>
    </div>
  )
}

/** The code the slip prints, drawn as a mark: the real one is minted at print. */
function QrMark() {
  return (
    <svg className="rx-slip__code" width={72} height={72} viewBox="0 0 21 21" aria-hidden="true">
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M0 0h7v7H0zM1 1v5h5V1zM2 2h3v3H2zM14 0h7v7h-7zm1 1v5h5V1zm1 1h3v3h-3zM0 14h7v7H0zm1 1v5h5v-5zm1 1h3v3H2zM9 0h2v2H9zM9 3h1v3H9zM11 4h2v2h-2zM8 8h2v2H8zM11 8h3v1h-3zM15 9h2v2h-2zM18 8h3v2h-3zM9 11h2v3H9zM12 11h3v2h-3zM8 15h2v2H8zM11 15h1v6h-1zM13 14h3v2h-3zM17 13h2v3h-2zM14 17h2v4h-2zM18 18h3v1h-3zM20 15h1v2h-1zM17 20h2v1h-2z"
      />
    </svg>
  )
}

export type SlipLanguage = LanguageCode

/** Values or problems by field key. */
type Values = Record<string, string>

/** One declared name or phone value of the customer being prepared, by its own label. */
function DetailField({ field, value, problem, onChange }: { field: VisitField; value: string; problem: string; onChange: (v: string) => void }) {
  const { lang, say } = useStaffText()
  const limit = limitOf(field)
  return (
    <TextField className="rx-field" variant="secondary" value={value} onChange={onChange} isInvalid={problem !== ''}>
      <Label>{nameIn(field.name, lang, field.key)}</Label>
      <Input
        placeholder={say('reception.optionalHint')}
        autoComplete="off"
        inputMode={field.type === 'phone' ? 'tel' : undefined}
        maxLength={limit}
      />
      {problem && <span className="rx-problem">{problem}</span>}
    </TextField>
  )
}

/** The customer being prepared, above the services: name, mobile, priority, slip language, a note. */
export function CustomerFields({
  name,
  phone,
  values,
  problems,
  onValue,
  priorityOffered,
  priority,
  onPriority,
  language,
  languages,
  onLanguage,
  note,
  onNote,
}: {
  /** The declared text and phone fields; none when not declared or not readable here. */
  name?: VisitField
  phone?: VisitField
  values: Values
  problems: Values
  onValue: (key: string, value: string) => void
  priorityOffered: boolean
  priority: Priority
  onPriority: (p: Priority) => void
  language: SlipLanguage
  /** The organisation's customer languages; one hides the choice. */
  languages: readonly SlipLanguage[]
  onLanguage: (l: SlipLanguage) => void
  note: string
  onNote: (v: string) => void
}) {
  const { say } = useStaffText()
  return (
    <div className="rx-cust">
      {[name, phone].map(
        (f) =>
          f && <DetailField key={f.key} field={f} value={values[f.key] ?? ''} problem={problems[f.key] ?? ''} onChange={(v) => onValue(f.key, v)} />,
      )}
      <PriorityChoice offered={priorityOffered} value={priority} onChange={onPriority} />
      {languages.length > 1 && (
        <Select className="rx-field" variant="secondary" value={language} onChange={(next) => next != null && onLanguage(String(next) as SlipLanguage)}>
          <Label>{say('reception.ticketLanguage')}</Label>
          <Select.Trigger>
            <Select.Value />
            <Select.Indicator />
          </Select.Trigger>
          <Select.Popover>
            <ListBox>
              {languages.map((l) => (
                <ListBox.Item key={l} id={l} textValue={nativeName(l)} lang={l}>
                  {nativeName(l)}
                  <ListBox.ItemIndicator />
                </ListBox.Item>
              ))}
            </ListBox>
          </Select.Popover>
        </Select>
      )}
      <TextField className="rx-field rx-cust__note" variant="secondary" value={note} onChange={onNote}>
        <Label>{say('reception.visitNote')}</Label>
        <Input maxLength={NOTE_LIMIT} placeholder={say('reception.notePlaceholder')} autoComplete="off" />
      </TextField>
    </div>
  )
}
